import { useState, type ReactNode } from 'react'

/// Generic accordion section for the CommandPanel rail.
///
/// Header is a full-width button showing the label plus a chevron
/// that rotates 90° when the section is open. Body is conditionally
/// rendered when open — no height animation, no spring, no
/// choreography. The toggle feels instant, which is what we want
/// for an ATC command surface where the player expects zero wait
/// states.
///
/// Sections do NOT communicate with each other. Two siblings can
/// be open simultaneously (non-strict accordion). The parent
/// container resets section state on aircraft selection change
/// via `key={aircraft.id}`.
export interface CollapsibleSectionProps {
  label: string
  defaultOpen?: boolean
  children: ReactNode
}

export function CollapsibleSection(props: CollapsibleSectionProps) {
  const [open, setOpen] = useState<boolean>(props.defaultOpen ?? false)

  return (
    <div className="rounded-md bg-slate-800/40 ring-1 ring-white/5">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-300 hover:text-white"
      >
        <span>{props.label}</span>
        <span
          aria-hidden="true"
          className={
            'inline-block transition-transform duration-150 ease-out ' +
            (open ? 'rotate-90' : 'rotate-0')
          }
        >
          ▸
        </span>
      </button>
      {open && <div className="px-3 pb-3 pt-1">{props.children}</div>}
    </div>
  )
}
